/**
 * Password Change Guard Component
 *
 * Guard for protected routes that blocks access when password change is required.
 * Redirects users with passwordChangeRequired flag to /change-password page.
 * Used as a wrapper around protected routes that should be inaccessible during forced password change.
 */

import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { usePasswordChangeRequired } from '../hooks/useAuthHooks';

/**
 * PasswordChangeGuard - Blocks access to routes when password change is required
 *
 * Behavior:
 * - If passwordChangeRequired is true: redirect to /change-password
 * - If passwordChangeRequired is false: render child routes (Outlet)
 *
 * Usage:
 * Wrap protected routes that should be blocked during password change:
 * <Route element={<AuthGuard />}>
 *   <Route path="/change-password" element={<ChangePasswordPage />} />
 *   <Route element={<PasswordChangeGuard />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *     <Route path="/invoices" element={<InvoicesPage />} />
 *   </Route>
 * </Route>
 */
const PasswordChangeGuard: React.FC = () => {
  const passwordChangeRequired = usePasswordChangeRequired();

  if (passwordChangeRequired) {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
};

export default PasswordChangeGuard;
